import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef, useState } from "react";

import {
	aggridHeaderClass,
	commonBorderStyle,
	commonNumberCellStyle,
	commonTextCellStyle,
} from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { defaultItem } from "src/dtos/item/itemDto";

import { SAL2100_R01ItemGroupDto } from "../Dto_SAL2100_R01";

type ItemGroupGridRow = SAL2100_R01ItemGroupDto & { inserted?: boolean };

const formatNumber = (value: number | null | undefined) =>
	Number(value ?? 0).toLocaleString("ko-KR", { maximumFractionDigits: 2 });

export const ItemGroupTable_SAL2100_R01 = ({ data }: { data: SAL2100_R01ItemGroupDto[] }) => {
	const gridRef = useRef<AgGridReact<ItemGroupGridRow>>(null);
	const [, setHoveredRowIndex] = useState<number | null>(null);
	const [, setColumnState] = useState<ColumnState[]>([]);
	const [scrollTop, setScrollTop] = useState<number | null>(null);
	const rowData = useMemo<ItemGroupGridRow[]>(() => data.map((item) => ({ ...item })), [data]);
	const columnDefs = useMemo<ColDef<ItemGroupGridRow>[]>(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "품목그룹",
			field: "itemGroupNm",
			colId: "itemGroupNm",
			flex: 1.5,
			minWidth: 120,
			headerClass: aggridHeaderClass,
			cellStyle: () => ({ ...commonBorderStyle, ...commonTextCellStyle, textAlign: "center" }),
			valueGetter: (params) => params.data?.itemGroupNm || params.data?.itemGroup || "미지정",
		},
		{
			headerName: "매출액",
			field: "salesAmount",
			colId: "salesAmount",
			flex: 1.5,
			minWidth: 120,
			headerClass: aggridHeaderClass,
			cellStyle: () => ({ ...commonBorderStyle, ...commonNumberCellStyle }),
			valueFormatter: (params) => formatNumber(params.value),
		},
	], []);
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(columnDefs, []),
		[columnDefs],
	);
	const totalRow = useMemo<ItemGroupGridRow[]>(() => [{
		...defaultItem,
		itemGroupNm: "합계",
		salesAmount: data.reduce((sum, item) => sum + item.salesAmount, 0),
	}], [data]);

	return (
		<GridContextWrapper<ItemGroupGridRow>
			gridRef={gridRef}
			paddedGridData={rowData}
			initSelected={() => {}}
			defaultRowData={{ ...defaultItem, itemGroupNm: "", salesAmount: 0 }}
			baseColumnDefs={columnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={(state) => setColumnState(state ?? [])}
			localStorageKey="SAL2100_R01-itemGroup-grid-column-state"
			menuItems={undefined}
			enableDataHistory={false}
		>
			<Grid_Touch<ItemGroupGridRow>
				ref={gridRef}
				rowData={rowData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				domLayout="autoHeight"
				getRowId={(params) => params.data.itemGroup || params.data.itemGroupNm}
				onCellKeyDown={undefined}
				pinnedBottomRowData={totalRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey="SAL2100_R01-itemGroup-grid-column-state"
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				enableCellTextSelection
			/>
		</GridContextWrapper>
	);
};
